'use client';

import { X } from 'lucide-react';
import Image from 'next/image';
import { useEffect } from 'react';

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  initialIndex?: number;
}

export function ImageModal({ isOpen, onClose, images, initialIndex = 0 }: ImageModalProps) {
  // Handle keyboard navigation and close on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
      <button
        onClick={onClose}
        className="absolute right-4 top-4 z-10 rounded-full bg-[var(--card)]/20 p-2 text-white hover:bg-[var(--card)]/30 transition-colors backdrop-blur-sm"
        aria-label="Close modal"
      >
        <X className="h-6 w-6" />
      </button>

      <div className="relative h-full w-full max-w-6xl">
        <div className="flex h-full items-center justify-center">
          <Image
            src={images[initialIndex]}
            alt=""
            fill
            className="object-contain p-4"
            priority
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 60vw"
          />
        </div>

        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const prevIndex = (initialIndex - 1 + images.length) % images.length;
                // Update the URL without triggering a navigation
                window.history.replaceState({}, '', `#image-${prevIndex}`);
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-[var(--card)]/20 p-2 text-white hover:bg-[var(--card)]/30 transition-colors backdrop-blur-sm"
              aria-label="Previous image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const nextIndex = (initialIndex + 1) % images.length;
                // Update the URL without triggering a navigation
                window.history.replaceState({}, '', `#image-${nextIndex}`);
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-[var(--card)]/20 p-2 text-white hover:bg-[var(--card)]/30 transition-colors backdrop-blur-sm"
              aria-label="Next image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Thumbnail strip */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2 overflow-x-auto p-2">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  // Update the URL without triggering a navigation
                  window.history.replaceState({}, '', `#image-${idx}`);
                }}
                className={`h-16 w-16 shrink-0 overflow-hidden rounded border-2 transition-all ${
                  idx === initialIndex ? 'border-white' : 'border-transparent'
                }`}
              >
                <Image
                  src={img}
                  alt={`Thumbnail ${idx + 1}`}
                  width={64}
                  height={64}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
